import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Sparkles } from 'lucide-react';
import type { TaskBoard } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { attachFilesToTask, withinSizeLimit } from './task-files';
import { useFileDrop } from '@/components/useFileDrop';
import { cn } from '@/lib/utils';
import { clipboardFiles, pendingKey } from './clipboard-files';
import { PendingFiles, type PendingFile } from './PendingFiles';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, type PickedMention } from './mention-text';
import { ContextPicker } from './TaskPickers';
import { ContextChip, LabelChip } from './task-ui';
import { TaskFieldControls } from './TaskFieldControls';
import { createTaskNow, type DraftContext } from './task-create';
import { capturePage, readAttachScreenshot, saveAttachScreenshot } from './page-screenshot';

export type { DraftContext } from './task-create';

type NewTaskProps = {
  meta: Pick<TaskBoard, 'members' | 'labels' | 'columns' | 'types'> & { ai_builder?: boolean };
  initialContexts?: DraftContext[];
  /** Filled in when the form opens from a suggestion. */
  initialTitle?: string;
  initialText?: string;
  currentPage?: DraftContext | null;
  onCreated?: (taskId: string) => void;
};

/** Create a task, optionally pre-linked to the page you're on. */
/** The type choice that leaves it to Jev (T-331). */
const SUGGEST_TYPE = 'suggest';

export function NewTaskDialog({
  open,
  onOpenChange,
  ...props
}: NewTaskProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {/* The dialog's content unmounts when it closes, so each opening starts a blank form. */}
        <NewTaskForm {...props} onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}

function NewTaskForm({
  meta,
  initialContexts = [],
  initialTitle = '',
  initialText = '',
  currentPage,
  onCreated,
  onClose,
}: NewTaskProps & { onClose: () => void }) {
  const firstOpen = meta.columns.find((c) => !c.is_done) ?? meta.columns[0];
  const [title, setTitle] = useState(initialTitle);
  const [text, setText] = useState(initialText);
  const [mentions, setMentions] = useState<PickedMention[]>([]);
  const [columnId, setColumnId] = useState<string | undefined>(firstOpen?.id);
  const [assignees, setAssignees] = useState<string[]>([]);
  const [labels, setLabels] = useState<string[]>([]);
  // Left on Suggest, Jev picks the type from the title and description once it's saved (T-331).
  const [typeId, setTypeId] = useState<string>(SUGGEST_TYPE);
  const [contexts, setContexts] = useState<DraftContext[]>(initialContexts);
  const [files, setFiles] = useState<PendingFile[]>([]);
  // Opened from a page: optionally attach a picture of it (remembered, off at first).
  const [screenshot, setScreenshot] = useState(readAttachScreenshot);
  const offerScreenshot = Boolean(currentPage);

  /** Closes at once: the task shows (and opens) before the server has saved it. */
  const submit = () => {
    const column = meta.columns.find((c) => c.id === columnId) ?? firstOpen;
    if (!title.trim() || !column) return;
    const { id } = createTaskNow({
      title: title.trim(),
      column,
      description: displayToMarkup(text.trim(), mentions),
      assignee_ids: assignees,
      label_ids: labels,
      contexts,
      type_id: typeId === SUGGEST_TYPE ? undefined : typeId,
      defaultTypeId: meta.types.find((t) => t.is_default)?.id,
    });
    onClose();
    onCreated?.(id);
    const picked = files.map((f) => f.file);
    // Files go up as the first message; that request waits for the task to exist. The page
    // screenshot leads it. The dialog is outside the captured area, so it isn't in the picture.
    if (offerScreenshot && screenshot) {
      void capturePage()
        .catch((error: unknown) => {
          console.error('[tasks] page screenshot failed', error);
          return null;
        })
        .then((shot) => {
          if (!shot) toast.error('Couldn’t take a screenshot of the page');
          const all = shot ? [shot, ...picked] : picked;
          if (all.length) attachFilesToTask(id, all);
        });
    } else if (picked.length) {
      attachFilesToTask(id, picked);
    }
  };

  const addFiles = (next: File[]) => {
    const ok = withinSizeLimit(next);
    setFiles((prev) => [
      ...prev,
      ...ok.map((file) => ({ key: pendingKey(file), file, attachmentId: null, uploading: false })),
    ]);
  };
  const drop = useFileDrop(addFiles);

  return (
    <>
      <DialogHeader>
        <DialogTitle>New task</DialogTitle>
      </DialogHeader>
      <div
        className={cn(
          'space-y-3 rounded-md',
          drop.dragging && 'outline-2 outline-offset-4 outline-dashed outline-primary'
        )}
        {...drop.bind}
        onPaste={(e) => {
          if (e.defaultPrevented) return;
          const pasted = clipboardFiles(e.clipboardData);
          if (!pasted.length) return;
          e.preventDefault();
          addFiles(pasted);
        }}
      >
        <Input
          aria-label="Title"
          autoFocus
          value={title}
          placeholder="What needs doing?"
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        <MentionTextarea
          aria-label="Details"
          tagging
          value={text}
          onChange={setText}
          mentions={mentions}
          onMentionsChange={(next) => {
            const people = next.filter(
              (m) => m.type === 'user' && !mentions.some((p) => p.type === 'user' && p.id === m.id)
            );
            if (people.length) setAssignees((prev) => [...new Set([...prev, ...people.map((m) => m.id)])]);
            setMentions(next);
          }}
          members={meta.members}
          aiBuilder={meta.ai_builder ?? false}
          rows={4}
          placeholder="Details (optional)… paste or drop screenshots, @ to mention someone or something, # to tag"
          onSubmit={() => submit()}
          onFiles={addFiles}
        />
        <PendingFiles items={files} onRemove={(item) => setFiles((prev) => prev.filter((f) => f.key !== item.key))} />
        {offerScreenshot ? (
          <label className="flex w-fit items-center gap-2 text-sm text-muted-foreground">
            <Checkbox
              checked={screenshot}
              onCheckedChange={(on) => {
                setScreenshot(on === true);
                saveAttachScreenshot(on === true);
              }}
            />
            Attach a screenshot of this page
          </label>
        ) : null}
        <TaskFieldControls
          meta={meta}
          value={{ columnId: columnId ?? '', typeId, assigneeIds: assignees, labelIds: labels }}
          onChange={(next) => {
            setColumnId(next.columnId);
            setTypeId(next.typeId);
            setAssignees(next.assigneeIds);
            setLabels(next.labelIds);
          }}
          typeLead={{
            value: SUGGEST_TYPE,
            label: (
              <>
                <Sparkles className="text-ai" />
                Type: suggest
              </>
            ),
          }}
        >
          <ContextPicker
            currentPage={
              currentPage && !contexts.some((c) => c.type === currentPage.type && c.id === currentPage.id)
                ? currentPage
                : null
            }
            onPick={(ref) =>
              setContexts((prev) =>
                prev.some((c) => c.type === ref.type && c.id === ref.id)
                  ? prev
                  : [...prev, { ...ref, title: ref.title ?? ref.id, url: '' }]
              )
            }
          >
            <Button variant="outline" size="sm">
              <Plus className="size-4" />
              Link
            </Button>
          </ContextPicker>
        </TaskFieldControls>
        {labels.length || contexts.length ? (
          <div className="flex flex-wrap gap-1.5">
            {labels.map((id) => {
              const label = meta.labels.find((l) => l.id === id);
              return label ? <LabelChip key={id} label={label} /> : null;
            })}
            {contexts.map((c) => (
              <ContextChip
                key={`${c.type}:${c.id}`}
                context={c}
                onRemove={() => setContexts((prev) => prev.filter((x) => !(x.type === c.type && x.id === c.id)))}
              />
            ))}
          </div>
        ) : null}
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!title.trim()} onClick={() => submit()}>
          Create task
        </Button>
      </DialogFooter>
    </>
  );
}
